import React, {Component, ReactNode} from 'react';
import {query, repository, subscribe} from "@trlogic/client-js";
import {TransactionData} from "../../../../domain/stream/transaction/TransactionDomains";
import NavigationUtil from "../../../../util/NavigationUtil";
import {CaseAttachmentLog, CaseCommentLog, CaseEventLog, CaseTaggedFieldLog} from "../../../../domain/case/CaseDomains";
import {InstanceContainer} from "../../../../config/binding/InstanceBinding";
import Injectables from "../../../../constant/Injectables";
import {Dialog} from "primereact/dialog";
import CaseAttachmentChatComponent from "./CaseAttachmentChatComponent";
import Icon from "../../../../icons/Icon";
import _ from "lodash";
import LanguageUtil from "../../../../util/LanguageUtil";
import moment from "moment";
import QueryBuilder = query.QueryBuilder;
import Computation = subscribe.Computation;
import SubscriptionContext = subscribe.SubscriptionContext;

class CaseAttachmentContainerComponent extends Component<Props, State> {

  public state: State = new State();

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  private computation: Computation | undefined;
  private readonly subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);

  public render(): ReactNode {
    const {visible, onClose} = this.props;
    return (
      <div>
        <Dialog header={this.getHeader} className={"case-attachment w-2/5 h-3/5"} visible={visible}
                onHide={() => {
                  this.stopTracker();
                  onClose()
                }}
                closable={false}
                closeOnEscape={true} dismissableMask={true}>
          {this.getContent()}
        </Dialog>
      </div>
    );
  }

  public componentDidUpdate() {
    this.startTracker();
  }

  private startTracker = () => {
    const collections = this.subscriptionContext.getSubscriptionsByCollection(this.getEndpoint());
    this.computation = subscribe.Tracker.autorun(this.tracker, collections)
  }

  private tracker = () => {
    void this.buildCommentLog();
  }

  private stopTracker = () => {
    this.computation?.stop();
  }

  private buildCommentLog = async () => {
    const raw = await this.repository.findMany<CaseCommentLog>(this.getQuery());
    const edits = raw.filter((a) => a.type === "COMMENT_EDIT");
    const latestEditByOriginal: Record<string, any> = {};
    edits.forEach((e: any) => {
      const originalId = e.originalCommentId;
      if (!originalId) return;
      const current = latestEditByOriginal[originalId];
      if (!current || (e.timestamp ?? 0) > (current.timestamp ?? 0)) {
        latestEditByOriginal[originalId] = e;
      }
    });

    const attachments = raw
      .filter((a) => a.type !== "COMMENT_EDIT")
      .map((a) => {
        if (a.type === "COMMENT" && latestEditByOriginal[a.id]) {
          return { ...a, content: latestEditByOriginal[a.id].content };
        }
        return a;
      });

    const filtered = attachments.filter(
      (attachment) => attachment.type === "COMMENT" || attachment.type === "TAGGED_FIELD" || attachment.type === "ATTACHMENT"
    );
    if (!_.isEqual(attachments, this.state.attachments)) {
      this.props.setCommentCount(filtered.length);
      this.setState({attachments})
    }
  }

  private getEndpoint = () => {
    return `case_event_log_${NavigationUtil.getTenant()}_*`
  }

  private getQuery = () => {
    const endpoint = this.getEndpoint();
    return QueryBuilder.select()
      .from(endpoint)
      .limit(1000)
      .orderBy({
        field: "timestamp",
        operator: "ASC"
      })
      .build()
  }

  private getHeader = () => {
    return (
      <div className={"bg-[#333333] h-[54px] rounded-t-[6px] flex items-center justify-between align-center pl-[20px] pr-[20px]"}>
        <div className={"flex items-center justify-start align-center w-full lg:w-auto gap-2"}>
          <div className={"bg-white rounded-full w-[34px] h-[34px] min-h-[34px] min-w-[34px] shrink-0 flex justify-center align-center"}>
            <div className={"text-f-purple"}>
              <Icon icon={"comment"} size={20}/>
            </div>
          </div>
          <div className={"text-white font-bold text-md"}>
            {LanguageUtil.get({id: "common.label.chat", defaultMessage: "Chat"})}
          </div>
        </div>
        <div
          className={'bg-white w-5 h-5 rounded-[4px] flex items-center justify-center border-[0.8px] border-[#E0E4E8] hover:bg-f-gray active:border-[#458EF7] hover:cursor-pointer z-50'}
          onClick={this.exportToCSV}
        >
          <div className={'w-3 h-3 flex items-center justify-center'}>
            <Icon icon={"export"}/>
          </div>
        </div>
      </div>
    )
  }

  private exportToCSV: () => void = (): void => {
    const {attachments} = this.state;
    const {transaction} = this.props;

    if (!attachments || attachments.length === 0) {
      return;
    }

    const headers: string[] = [
      'Timestamp',
      'Type',
      'User',
      'Content',
      'Transaction ID'
    ];

    const csvRows: string[] = [headers.join(',')];

    attachments.forEach((attachment): void => {
      let content: string = '';

      if (attachment.type === 'COMMENT' && 'content' in attachment) {
        content = (attachment as CaseCommentLog).content || '';
      } else if (attachment.type === 'TAGGED_FIELD' && 'taggedField' in attachment && 'taggedValue' in attachment) {
        const taggedLog = attachment as CaseTaggedFieldLog;
        content = `Field: ${taggedLog.taggedField}, Value: ${taggedLog.taggedValue}`;
      } else if (attachment.type === 'ATTACHMENT' && 'attachments' in attachment) {
        const attachmentLog = attachment as CaseAttachmentLog;
        content = attachmentLog.attachments?.map(att => att.fileName).join(', ') || '';
      }

      const row: string[] = [
        attachment.timestamp ? moment(attachment.timestamp).format('YYYY-MM-DD HH:mm:ss') : '',
        attachment.type || '',
        attachment.username || '',
        content ? `"${content.replace(/"/g, '""')}"` : '',
        transaction.id || ''
      ];
      csvRows.push(row.join(','));
    });

    const csvContent: string = csvRows.join('\n');
    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url: string = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `case_attachments_${transaction.id}_${moment().valueOf()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  private getContent = () => {
    const {attachments} = this.state;
    const {domain, transaction, disabled} = this.props;
    return <CaseAttachmentChatComponent disabled={disabled} attachments={attachments as CaseCommentLog[] | CaseAttachmentLog[]} id={transaction.id}
                                        transaction={transaction} domain={domain}/>
  }
}

interface Props {
  visible: boolean;
  onClose: () => void;
  transaction: TransactionData;
  domain: string | undefined;
  disabled: boolean
  setCommentCount: (count: number) => void;
}

class State {
  loading: boolean = true;
  attachments: CaseEventLog[] = [];
  tab: "COMMENT" | "ATTACHMENT" = "COMMENT";
}

export default CaseAttachmentContainerComponent;
